<script setup lang="ts">
// IDS-188: custom component for supplier flyout
import type { Schemas } from '@shopware/api-client/api-types';
import { getCategoryRoute } from '@shopware-pwa/helpers-next';

const props = defineProps<{
    navigationElement: Schemas['Category'];
    supplierId: string;
}>();

const { getSupplierById } = useSupplier();
const { data: supplier } = await getSupplierById(props.supplierId);
const {  linkNewTab, externalLink } = useNavigationElement(
    props.navigationElement,
);
const { resolveLink, isInternalLink, isExternalLink } = useNavigationLinkResolver(
    props.navigationElement,
);

const categoryLink = computed(() => {
    if (isExternalLink && externalLink) {
        return externalLink;
    }

    if (isInternalLink) {
        const link = resolveLink();
        return link.path;
    }

    return props.navigationElement ? getCategoryRoute(props.navigationElement) : '';
});
</script>

<template>
    <!-- IDS-188: add image (if available) -->
    <template v-if="supplier">
        <div class="flex h-32 items-center justify-center bg-neutral-300 p-4">
            <LocaleLink
                v-if="supplier.media"
                :target="isExternalLink || linkNewTab ? '_blank' : ''"
                :rel="isExternalLink || linkNewTab ? 'noopener noreferrer nofollow' : ''"
                :aria-label="$t('navigation.sidebar.findOutMore')"
                :to="categoryLink"
                :format="!isExternalLink"
            >
                <img
                    :src="supplier.media.url"
                    :alt="supplier.media.translated.alt"
                    class="max-h-16 max-w-32 object-contain"
                />
            </LocaleLink>
        </div>

        <!-- IDS-188: set hover to text color -->
        <NavigationLink
            :navigation-element="navigationElement"
            classes="text-lg font-bold py-2 !text-neutral-800 text-md"
            active-classes="text-brand-primary"
            :as-link="navigationElement.type !== 'folder'"
        />

        <div>
            <NavigationLink
                :navigation-element="navigationElement"
                classes="py-2 !text-neutral-800"
                active-classes="text-brand-primary"
                :use-slot="true"
            >
                {{ $t('navigation.sidebar.findOutMore') }}
            </NavigationLink>
        </div>
        <div>
            <NavigationLink
                v-if="navigationElement.children[0]"
                :navigation-element="navigationElement.children[0]"
                :as-all-items-link="true"
                classes="py-2 !text-neutral-800"
                active-classes="text-brand-primary"
            />
        </div>
    </template>
</template>
